import React, { useEffect, useReducer, useState } from 'react';
import { IconButton, Button, Checkbox, FormControlLabel, Tab, Tabs } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { createHotelPackage, updateHotelPackage } from '../../../../../utils/ServicesAPIUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { useDispatch, useSelector } from 'react-redux';
import { allHotelsSelector, fetchAllAccommodations } from '../../../../property/propertySlice';
import './Package-Create.scss';
import { formDataValidate } from '../../../../../utils/formUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import { ASSET_PACKAGES } from '../../../../../constants/PropertyConst';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';

const PackageCreate = (props) => {
  const { toggleDrawer, packageObj, hotelId, showLoader = () => {} } = props;
  const dispatch = useDispatch();
  const originalHotels = useSelector(allHotelsSelector);
  const groupId = originalHotels?.find((h) => h?.id === hotelId)?.groupId;
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [rawImages, setRawImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Package Name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Package Code',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    costPerNight: {
      value: '',
      fieldName: 'Cost per night',
      errors: {},
      validations: ['required', 'positiveOnlyWithZero'],
      tab: 0,
    },
    isActive: {
      value: false,
      fieldName: '',
      errors: {},
      validations: [],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  useEffect(() => {
    if (packageObj) {
      const currFields = { ...formData };
      for (let field in currFields) {
        if (field === 'images') {
          currFields[field].value = packageObj.images || [];
        } else {
          currFields[field].value = packageObj[field];
        }
      }
      setFormData(currFields);
    }
  }, [packageObj]);

  const handleActiveChange = (event) => {
    const currField = { ...formData.isActive };
    currField.value = event.target.checked;
    setFormData({ isActive: currField });
  };

  const validate = (tab) => {
    const updatedFields = {};
    const currTabFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tab),
    );
    const isValid = formDataValidate(currTabFormData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const onImageListUpdated = (imgObjArr) => {
    setRawImages(imgObjArr);
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...formData.images.value];
    images.splice(index, 1);
    setFormData({ images: { ...formData.images, value: images } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate(0)) {
      try {
        dispatch(stepUpLoaderCount());
        setLoading(true);
        toggleDrawer('', false, '');
        const selectedLanguage = decryptLocalStorage('lang');
        const language = selectedLanguage !== 'en' ? selectedLanguage : '';
        const payload = {};

        for (let field in formData) {
          if (field === 'images') continue;
          payload[field] = formData[field].value;
        }

        // Process image upload
        payload.images = await getProcessedImages(
          rawImages,
          groupId,
          ASSET_PACKAGES,
          hotelId,
        );

        if (packageObj) {
          payload.id = packageObj.id;
          await updateHotelPackage(payload, hotelId, language);
          notificationManager('success', 'Package updated successfully');
        } else {
          await createHotelPackage(payload, hotelId, language);
          notificationManager('success', 'Package created successfully');
        }

        dispatch(fetchAllAccommodations(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          packageObj
            ? notificationManager('danger', 'Package updation failed')
            : notificationManager('danger', 'Package creation failed');
        }
      } finally {
        setLoading(false);
        dispatch(stepDownLoaderCount());
      }
    } else {
      notificationManager('danger', 'Enter all required fields');
      setTabValue(0);
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else if (validate(tabValue)) {
      setTabValue(newValue);
    }
  };

  const onNextClick = () => {
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  useEffect(() => {
    const currTabFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tabValue),
    );
    const isValid = formDataValidate(currTabFormData, false);
    setIsTabInValid(!isValid);
  }, [formData, tabValue]);

  return (
    <form className='package-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-4'>
            <span className='page-title'>{packageObj ? 'Edit Package' : 'Add Package'}</span>
          </div>
          <div className='col-md-7'>
            <Tabs
              value={tabValue}
              variant='fullWidth'
              indicatorColor='primary'
              onChange={handleTabChange}
              aria-label='package-add-tabs'
              textColor='primary'
              className='property-tabs'
            >
              <Tab label='Details' />
              <Tab label='Media' />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          {tabValue === 0 && (
            <>
              <div className='row px-3 w-100 mt-4'>
                <div className='col-md-6'>
                  <div className='page-title-secondary'>Details</div>
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <CustomTextField
                      item={formData.name}
                      handleInputChange={(e) => {
                        setFormData({ name: e });
                      }}
                      maxLength={100}
                      endAdornment={true}
                      characterCount={true}
                      multiline={true}
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <CustomTextField
                      item={formData.code}
                      handleInputChange={(e) => {
                        setFormData({ code: e });
                      }}
                      multiline={true}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-12 mb-3'>
                    <CustomTextField
                      item={formData.description}
                      multiline={true}
                      handleInputChange={(e) => {
                        setFormData({ description: e });
                      }}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <CustomTextField
                      type='number'
                      item={formData.costPerNight}
                      handleInputChange={(e) => {
                        setFormData({ costPerNight: e });
                      }}
                      min={0}
                      max={99999999}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-md-6 mb-3 mx-2'>
                  <FormControlLabel
                    control={
                    <Checkbox
                      checked={formData.isActive.value}
                      onChange={handleActiveChange}
                      className='status-checkbox'
                      color='primary'
                    />}
                      label='Active'
                   />
                    {' '}
                  </div>
                </div>
              </div>
            </>
          )}
          {tabValue === 1 && (
            <div className='row px-3 w-100 d-flex mt-4'>
              <div className='col-md-12 mb-3'>
                <ImageUpload
                  uploadedImages={formData.images.value}
                  imgList={rawImages}
                  onImageListUpdated={onImageListUpdated}
                  onUploadedImageRemoval={onUploadedImageRemoval}
                  onImagesLoading={(status) => {
                    setLoading(status);
                  }}
                  hotelId={hotelId}
                  groupId={groupId}
                  propertyType={ASSET_PACKAGES}
                  enabledMediaTabs={['image']}
                />
              </div>
            </div>
          )}
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 1 ? (
              <Button
                variant='contained'
                color='primary'
                onClick={onNextClick}
                size='large'
                className='me-3'
                disabled={isTabInValid}
              >
                Next
              </Button>
            ) : (
              <Button
                disabled={loading}
                onClick={handleSubmit}
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
              >
                {packageObj ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};

export default PackageCreate;
